import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CreditCard, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { PAY_PACKAGES, useLang } from "@/i18n";
import { API_BASE as API, PAYPAL_CLIENT_ID } from "@/config";

const PAYPAL_VISIBLE = true;
const EASE = [0.16, 1, 0.3, 1];

const PKG_NAMES = {
  permit_priority: { ar: "معالجة عاجلة لتصاريح الطيران", en: "Flight Permit Priority Processing" },
  handling_deposit: { ar: "عربون المناولة الأرضية", en: "Ground Handling Deposit" },
  vip_deposit: { ar: "عربون حزمة كبار الشخصيات", en: "VIP Executive Deposit" },
};

function StripeCard({ pkg, lang, t }) {
  const [loading, setLoading] = useState(false);
  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(t("pay_status_failed"));
      setLoading(false);
    }
  };
  return (
    <div
      data-testid={`stripe-package-${pkg.id}`}
      className="flex items-center justify-between gap-4 rounded-2xl border border-blue-500/15 bg-[#081020] p-5 hover:border-blue-500/40 transition-colors duration-300"
    >
      <div>
        <div className="font-bold text-white">{PKG_NAMES[pkg.id][lang]}</div>
        <p className="mt-1 text-xs text-slate-400">{lang === "ar" ? pkg.desc_ar : pkg.desc_en}</p>
        <div className="mt-2 font-mono-tag text-2xl font-bold text-blue-400" dir="ltr">
          ${pkg.amount} <span className="text-xs text-slate-500 font-normal">USD</span>
        </div>
      </div>
      <button
        data-testid={`stripe-pay-${pkg.id}`}
        onClick={pay}
        disabled={loading}
        className="shrink-0 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-60 transition-colors duration-200"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
        {loading ? t("pay_processing") : t("pay_button")}
      </button>
    </div>
  );
}

function PayPalPanel({ t, lang }) {
  const [pkgId, setPkgId] = useState("handling_deposit");
  if (!PAYPAL_CLIENT_ID) {
    return (
      <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-blue-500/25 bg-[#081020] p-8 text-center" data-testid="paypal-pending-notice">
        <Lock size={22} className="text-blue-400" />
        <p className="max-w-sm text-sm text-slate-400">{t("pay_paypal_pending")}</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-blue-500/15 bg-[#081020] p-5" data-testid="paypal-panel">
      <select
        data-testid="paypal-package-select"
        value={pkgId}
        onChange={(e) => setPkgId(e.target.value)}
        className="mb-4 w-full rounded-xl border border-blue-500/20 bg-[#0B1526] px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
      >
        {PAY_PACKAGES.map((p) => (
          <option key={p.id} value={p.id}>{PKG_NAMES[p.id][lang]} — ${p.amount}</option>
        ))}
      </select>
      <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
        <PayPalButtons
          data-testid="paypal-buttons"
          style={{ layout: "vertical", color: "blue", shape: "pill", label: "paypal" }}
          forceReRender={[pkgId]}
          createOrder={async () => {
            const { data } = await axios.post(`${API}/payments/paypal/create-order`, { package_id: pkgId });
            return data.order_id;
          }}
          onApprove={async (d) => {
            try {
              await axios.post(`${API}/payments/paypal/capture/${d.orderID}`);
              toast.success(t("pay_status_success"));
            } catch {
              toast.error(t("pay_status_failed"));
            }
          }}
          onError={() => toast.error(t("pay_status_failed"))}
        />
      </PayPalScriptProvider>
    </div>
  );
}

export function Payments() {
  const { t, lang } = useLang();
  return (
    <section id="payments" data-testid="payments-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-12 max-w-3xl"
        >
          <span className="font-mono-tag text-xs tracking-[0.3em] uppercase text-blue-400">{t("pay_eyebrow")}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="payments-title">{t("pay_title")}</h2>
          <p className="mt-4 text-base text-slate-400">{t("pay_sub")}</p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck size={13} className="text-emerald-400" />
            {t("pay_secure")}
          </div>
        </motion.div>

        <div className={`grid gap-6 ${PAYPAL_VISIBLE ? "lg:grid-cols-2" : "lg:grid-cols-1 max-w-3xl"}`}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="space-y-4"
            data-testid="stripe-packages"
          >
            {PAY_PACKAGES.map((p) => (
              <StripeCard key={p.id} pkg={p} lang={lang} t={t} />
            ))}
          </motion.div>
          {PAYPAL_VISIBLE && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
            >
              <h3 className="mb-4 text-lg font-bold text-white">{t("pay_paypal_title")}</h3>
              <PayPalPanel t={t} lang={lang} />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
